import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { addLocale, locale } from 'primereact/api';
import { Badge } from 'primereact/badge';
import { Calendar } from 'primereact/calendar';
import { Card } from 'primereact/card';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabPanel, TabView } from 'primereact/tabview';
import { Toast } from 'primereact/toast';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  GlobalSettings,
  GlobalSettingsService
} from 'components/services/GlobalSettingsService';

import { TaskService } from '../../components/services/TaskService';
import { AssetTemplate, PriorityOptions } from '../../constants';
import { DEFAULT_GLOBAL_SETTINGS } from '../../constants';
import saveFile from '../../helpers/saveFile';
import { useTaskCardApi } from '../../hooks/services/useTaskCardApi';
import { ruLocale } from '../../lang/ruLocale';
import { RootState } from '../../state/store';
import {
  DropdownObjectType,
  Employee,
  Task,
  TreeNode
} from '../../types/interfaces';
import { TaskCardProps } from '../../types/props';
import TaskHistoryComponent from '../taskHistory/TaskHistoryComponent';

import '../css/TaskCard.css';
import AssetTab from './AssetTab';
import { useAssetOperations } from './hooks/useAssetOperations';
import { useNavigationBlocker } from './hooks/useNavigationBlocker';
import { useTaskCardDialogsState } from './hooks/useTaskCardDialogsState';
import {
  TASK_CREATED_DEPARTMENT_ASSIGNMENT_TOAST,
  useTaskCardSaveAndTransition
} from './hooks/useTaskCardSaveAndTransition';
import { useTaskDates } from './hooks/useTaskDates';
import { useTaskPrefetch } from './hooks/useTaskPrefetch';
import { MainTab } from './MainTab';
import OperationTabContent from './OperationTabContent';
import { defaultNode, defaultTaskCardProps } from './taskCardDefaults';
import { TaskCardDialogs } from './TaskCardDialogs';
import { TaskCardHeader } from './TaskCardHeader';
import { TaskCommentsComponent } from './TaskCommentsComponent';
import FileUpload from './Upload';

function rootLevelFieldErrorMessage(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'message' in err) {
    const m = (err as { message?: unknown }).message;
    return typeof m === 'string' ? m : undefined;
  }
  return undefined;
}

const TaskCard: React.FC<TaskCardProps> = ({
  taskProps = defaultTaskCardProps.taskProps
}) => {
  addLocale('ru', ruLocale.calendarCustomTranslate);
  locale('ru');

  const dispatch = useDispatch();
  const fieldFillingControll = useSelector(
    (state: RootState) => state.fieldFillingControll
  );
  const [activeIndex, setActiveIndex] = useState(0);

  const { pageId } = useParams();
  const isNewTask = pageId === 'newTask' || !pageId;
  const [searchParams, setSearchParams] = useSearchParams();
  const [hasNewComments, setHasNewComments] = useState(false);
  const [hasComments, setHasComments] = useState(false);
  const departmentId = searchParams.get('departmentId');
  const employeeId = searchParams.get('employeeId');
  const initialTabAppliedRef = useRef<boolean>(false);
  const [globalSettings, setGlobalSettings] = useState<GlobalSettings>();

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await GlobalSettingsService.get();
        setGlobalSettings(settings);
      } catch (error) {
        console.error('Ошибка загрузки настроек:', error);
      }
    };
    loadSettings();
  }, []);
  useEffect(() => {
    if (!initialTabAppliedRef.current) {
      const tabParam = searchParams.get('tab');
      if (tabParam === 'history') {
        setActiveIndex(4);
      }
      initialTabAppliedRef.current = true;
    }
  }, [searchParams]);

  const modifiedTaskProps = useMemo(() => {
    if (pageId === 'newTask') {
      const result = { ...taskProps };

      if (!!departmentId) {
        result.department = { key: departmentId, label: '' };
      }

      if (!!employeeId) {
        result.employees = [
          {
            id: employeeId,
            fullName: '',
            personnelNumber: ''
          }
        ];
      }

      return result;
    }
    return taskProps;
  }, [pageId, departmentId, employeeId, taskProps]);

  const {
    loading,
    departmentsDropDownValue,
    fullDepartmentsTree,
    fullExecutorsCatalog,
    showAllDepartments,
    toggleShowAllDepartments,
    showAllEmployees,
    toggleShowAllEmployees,
    departmentPickerError,
    employeePickerError,
    assetOptions,
    taskTypes,
    taskHistory,
    selectedPriority,
    setSelectedPriority,
    executorsTable,
    rawExecutorsData,
    plannedOperationsOptions,
    plannedOperationsAttainments,
    allOperationsAttainments,
    actualOperationsEmployeeOptions,
    fetchTaskTypeDetails,
    ensureTaskTypeDetails,
    selectedTaskTypeDetails,
    recommendedEmployeesCount,
    callApi,
    getAssetsApi,
    getFieldFillingControll,
    getTaskApi,
    form: {
      register,
      setValue,
      setError,
      trigger,
      handleSubmit,
      watch,
      getValues,
      reset,
      formState: { isDirty, dirtyFields, errors }
    }
  } = useTaskCardApi(
    modifiedTaskProps! as Task,
    pageId,
    activeIndex,
    globalSettings ?? DEFAULT_GLOBAL_SETTINGS
  );

  useEffect(() => {
    if (typeof taskProps?.id === 'number') {
      callApi(taskProps?.id).then(() => {
        const currentValues = getValues();
        reset(currentValues);
      });
    }
  }, [taskProps, callApi, getValues, reset]);

  useTaskPrefetch({
    isNewTask,
    departmentId,
    employeeId,
    setValue
  });

  useNavigationBlocker({ isDirty });

  const department = watch('department');
  const lastStatusTransitionId = watch('lastStatusTransitionId');

  const navigate = useNavigate();
  const [triggerApiCall, setTriggerApiCall] = useState<number | undefined>();

  const toast = useRef<Toast>(null);
  const tagScrollRef = useRef<HTMLDivElement>(null);
  const taskCodeCardRef = useRef<null | { handleCancel: () => void }>(null);
  const taskId = watch('id');
  const name = watch('name');
  const creationDate = watch('creationDate');
  const asset = watch('asset');
  const taskType = watch('taskType');
  const actualOperations = watch('actualOperations');
  const plannedOperations = watch('plannedOperations');
  const plannedStartDate = watch('plannedStartDate');
  const plannedEndDate = watch('plannedEndDate');
  const employees = watch('employees');
  const possibleStatusTransitions = watch('possibleStatusTransitions');
  const closeCode = watch('closeCode');
  const hasAttachments = watch('hasAttachments');
  const hasRouteAssignment = watch('hasRouteAssignment');

  const {
    attachmentsError,
    closingCodes,
    closeCodeDialogVisible,
    selectedAction,
    showEmployeesRecommendationDialog,
    recommendationDialogContext,
    onSubmitSchemaLoad,
    handleActionSelect,
    handleCodeDialogSave,
    saveDialog,
    closeClosingCodesDialog,
    onHideTaskCodeCard,
    dismissEmployeesRecommendationDialog,
    confirmEmployeesRecommendation,
    isDepartmentDeleted,
    setIsDepartmentDeleted
  } = useTaskCardSaveAndTransition({
    toast,
    navigate,
    dispatch,
    fieldFillingControll,
    getFieldFillingControll,
    getTaskApi,
    setTriggerApiCall,
    setValue,
    formState: { dirtyFields },
    setError,
    trigger,
    handleSubmit,
    recommendedEmployeesCount,
    taskCodeCardRef,
    actualOperations,
    plannedOperations,
    employees,
    possibleStatusTransitions,
    lastStatusTransitionId,
    hasAttachments,
    taskId,
    ensureTaskTypeDetails,
    selectedTaskTypeDetails
  });

  const {
    showDialog,
    setShowDialog,
    handleConfirmationCancel,
    handleConfirmationContinue,

    showDeleteDialog,
    setShowDeleteDialog,

    assetCardKey,
    setAssetCardKey,
    dialogVisible,
    setDialogVisible,
    assetData,
    setAssetData,
    assetCardRef,
    onHideAssetCard,
    closeAssetDialog,

    mapDialogVisible,
    mapDialogLoading,
    mapDialogError,
    mapTaskDetails,
    handleDeleteConfirmation,

    executorNodes,
    isDetailIdValid,
    openTaskMapDialog,
    closeTaskMapDialog
  } = useTaskCardDialogsState({
    navigate,
    taskId,
    employees,
    isNewTask
  });

  const actualStartDateRef = useRef<Calendar>(null);
  const plannedEndDateRef = useRef<Calendar>(null);
  const actualEndDateRef = useRef<Calendar>(null);
  const elementUIs = useSelector((state: RootState) => state.auth.elementUIs);
  const hasTaskRole = elementUIs.some(
    (ui) =>
      (ui.code === 'tasks-add-btn__roles' || ui.code === 'wholeui__roles') &&
      ui.visible
  );

  const [tagScrollHeight, setTagScrollHeight] = useState<number>(0);

  const isTaskDeleted = useMemo(() => {
    return !isNewTask && taskId === 0 && !loading;
  }, [isNewTask, taskId, loading]);

  const exportComments = useCallback(async () => {
    if (!hasComments) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Экспорт невозможен',
        detail: 'Примечаний нет, невозможно выгрузить данные',
        life: 4000
      });
      return;
    }

    const result = await TaskService.exportTaskComments(taskId);
    await saveFile(result.data, 'Comments.xls', '');
  }, [taskId, hasComments]);

  const exportHistory = useCallback(async () => {
    const result = await TaskService.exportTaskHistoryExcel(taskId);
    await saveFile(result.data, 'History.xls', '');
  }, [taskId]);

  useEffect(() => {
    if (tagScrollRef.current) {
      setTagScrollHeight(tagScrollRef.current.offsetHeight);
    }
  }, [plannedOperations]);

  useEffect(() => {
    const taskCreatedToast = localStorage.getItem('task_created_toast');
    const departmentAssignmentToast = localStorage.getItem(
      TASK_CREATED_DEPARTMENT_ASSIGNMENT_TOAST
    );
    if (toast.current) {
      if (
        taskCreatedToast &&
        taskId &&
        taskId.toString() === taskCreatedToast
      ) {
        setTimeout(() => {
          toast.current?.show({
            severity: 'success',
            summary: 'Регистрация наряда',
            detail: 'Регистрация наряда прошла успешно.',
            life: 3000
          });
          localStorage.removeItem('task_created_toast');

          if (departmentAssignmentToast) {
            try {
              const assignmentResult = JSON.parse(
                departmentAssignmentToast
              ) as {
                taskId?: string;
                assigned?: boolean;
                departmentLabel?: string;
              };

              if (assignmentResult.taskId === taskId.toString()) {
                const assignedDepartmentLabel =
                  assignmentResult.departmentLabel?.trim();
                toast.current?.show({
                  severity: assignmentResult.assigned ? 'success' : 'warn',
                  summary: 'Автораспределение участка',
                  detail: assignmentResult.assigned
                    ? `Участок назначен автоматически${
                        assignedDepartmentLabel
                          ? `: ${assignedDepartmentLabel}`
                          : '.'
                      }`
                    : 'Наряд зарегистрирован, участок не подобран автоматически.',
                  life: 5000
                });
                localStorage.removeItem(
                  TASK_CREATED_DEPARTMENT_ASSIGNMENT_TOAST
                );
              }
            } catch {
              localStorage.removeItem(TASK_CREATED_DEPARTMENT_ASSIGNMENT_TOAST);
            }
          }
        }, 10);
      }
    }
  }, [taskId]);

  const isUpdate =
    (taskId !== undefined && taskId !== null && taskId !== 0) ||
    (pageId !== undefined &&
      pageId !== null &&
      !isNewTask &&
      Number(pageId) !== 0);

  const handleDeleteExecutor = (taskId: string | number) => {
    const newEmployees = employees.filter(
      (employee) => employee.id.toString() !== taskId?.toString()
    );
    setValue('employees', newEmployees, {
      shouldDirty: true,
      shouldValidate: true
    });
  };

  const handleDetelePlannedJob = (taskId: string | number) => {
    const x = plannedOperations.filter(
      (department) => department.key?.toString() !== taskId?.toString()
    );
    setValue('plannedOperations', x, {
      shouldDirty: true,
      shouldValidate: true
    });
  };

  const handleCancel = () => {
    if (isDirty) {
      setShowDialog(true);
    }
    if (!isDirty) {
      navigate('/');
    }
  };

  const handleDistrictSelectionChange = (department: TreeNode) => {
    setValue('department', department, {
      shouldDirty: true,
      shouldValidate: true
    });
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const handleCreateClick = () => {
    setAssetData(AssetTemplate);
    setDialogVisible(true);
  };

  const handleSelectionChange = (
    selected: DropdownObjectType | DropdownObjectType[] | null
  ) => {
    if (Array.isArray(selected)) {
      setValue(
        'asset',
        selected.length > 0
          ? { key: String(selected[0].key), label: selected[0].label }
          : defaultNode,
        { shouldDirty: true, shouldValidate: true }
      );
      trigger('asset');
    } else {
      setValue(
        'asset',
        selected
          ? { key: String(selected.key), label: selected.label }
          : defaultNode,
        { shouldDirty: true, shouldValidate: true }
      );
      trigger('asset');
    }
  };

  const handleEditClick = (asset: TreeNode) => {
    setAssetData(asset.key ? { id: Number(asset.key), key: asset.key } : null);
    setDialogVisible(true);
  };

  const { handleAdd, handleUpdate } = useAssetOperations({
    getAssetsApi,
    setValue,
    setAssetCardKey
  });

  const {
    handleStartDateChange,
    handleEndDateChange,
    handlePlannedStartDateChange,
    CalendarHideHandle
  } = useTaskDates({ setValue, trigger });

  const mainErrors = useMemo(() => {
    if (
      errors.name ||
      errors.taskType ||
      errors.asset ||
      errors.department ||
      errors.employees ||
      errors.taskStatus ||
      errors.actualStartDate ||
      errors.plannedStartDate ||
      errors.plannedEndDate ||
      errors.actualEndDate ||
      errors.priority ||
      errors.description
    ) {
      return true;
    } else {
      return false;
    }
  }, [
    errors.name,
    errors.taskType,
    errors.asset,
    errors.department,
    errors.employees,
    errors.taskStatus,
    errors.actualStartDate,
    errors.plannedStartDate,
    errors.plannedEndDate,
    errors.actualEndDate,
    errors.priority,
    errors.description
  ]);

  const worksTabErrors = useMemo(() => {
    return !!(
      rootLevelFieldErrorMessage(errors.plannedOperations) ||
      rootLevelFieldErrorMessage(errors.actualOperations)
    );
  }, [errors.plannedOperations, errors.actualOperations]);

  useEffect(() => {
    if (triggerApiCall) {
      const executeApiCall = async () => {
        await callApi(triggerApiCall);
        setTriggerApiCall(undefined);
      };

      executeApiCall();
    }
  }, [triggerApiCall, getTaskApi, callApi]);

  const handleDataTableDropdownSelectionChange = (
    e: Employee[],
    options?: { markDirty?: boolean }
  ) => {
    if (Array.isArray(e)) {
      const markDirty = options?.markDirty !== false;
      setValue('employees', e, {
        shouldDirty: markDirty,
        shouldValidate: true
      });
    }
  };

  const cardTitleContent = (
    <TaskCardHeader
      toastRef={toast}
      isDetailIdValid={!!isDetailIdValid}
      taskId={taskId}
      taskType={taskType}
      name={name}
      asset={asset}
      creationDate={creationDate}
      isUpdate={isUpdate}
      isDirty={isDirty}
      activeIndex={activeIndex}
      onSubmitSchemaLoad={onSubmitSchemaLoad}
      onDelete={handleDelete}
      onOpenMap={openTaskMapDialog}
      onCancel={handleCancel}
      onExportComments={exportComments}
      onExportHistory={exportHistory}
    />
  );

  if (loading && taskId === 0 && !isNewTask) {
    return <ProgressSpinner />;
  }

  if (isTaskDeleted) {
    return (
      <div className="card">
        <Message className="w-full" severity="warn" text="Наряд удален" />
      </div>
    );
  }

  return (
    <Card
      title={cardTitleContent}
      style={{
        height: 'calc(100vh - 65px)',
        overflowY: 'auto',
        scrollbarGutter: 'stable both-edges'
      }}
      id="custom-cardscroll"
    >
      <div className="task-card task-card__roles">
        <Toast ref={toast} />
        <TabView
          panelContainerStyle={{ paddingBottom: 0 }}
          activeIndex={activeIndex}
          onTabChange={(e) => {
            setActiveIndex(e.index);
            setSearchParams((prevParam) => {
              const newParams = new URLSearchParams(prevParam);
              newParams.delete('tab');
              return newParams;
            });
          }}
        >
          <TabPanel header="Основная" className={mainErrors ? 'error-tab' : ''}>
            <MainTab
              fieldFillingControll={fieldFillingControll}
              creationDate={creationDate}
              errors={errors}
              taskId={taskId}
              taskTypes={taskTypes}
              taskType={taskType}
              assetOptions={assetOptions}
              asset={asset}
              setSelectedPriority={setSelectedPriority}
              department={department!}
              departmentsDropDownValue={departmentsDropDownValue}
              fullDepartmentsTree={fullDepartmentsTree}
              fullExecutorsCatalog={fullExecutorsCatalog}
              handleSelectionChange={handleSelectionChange}
              handleDistrictSelectionChange={handleDistrictSelectionChange}
              handleCreateClick={handleCreateClick}
              employees={employees}
              executorsTable={executorsTable}
              selectedPriority={selectedPriority}
              PriorityOptions={PriorityOptions}
              watch={watch}
              closeCode={closeCode}
              handleActionSelect={handleActionSelect}
              onToggleShowAll={toggleShowAllEmployees}
              showAllEmployees={showAllEmployees}
              possibleStatusTransitions={possibleStatusTransitions}
              selectedAction={selectedAction}
              handleStartDateChange={handleStartDateChange}
              actualStartDateRef={actualStartDateRef}
              CalendarHideHandle={CalendarHideHandle}
              handleEndDateChange={handleEndDateChange}
              actualEndDateRef={actualEndDateRef}
              plannedEndDateRef={plannedEndDateRef}
              loading={loading}
              register={register}
              setValue={setValue}
              fetchTaskTypeDetails={fetchTaskTypeDetails}
              executorNodes={executorNodes}
              handleDeleteTag={handleDeleteExecutor}
              handleDataTableDropdownSelectionChange={
                handleDataTableDropdownSelectionChange
              }
              handlePlannedStartDateChange={handlePlannedStartDateChange}
              recommendedEmployeesCount={recommendedEmployeesCount}
              showAllDepartments={showAllDepartments}
              toggleShowAllDepartments={toggleShowAllDepartments}
              departmentPickerError={departmentPickerError}
              employeePickerError={employeePickerError}
              isDepartmentDeleted={isDepartmentDeleted}
              setIsDepartmentDeleted={setIsDepartmentDeleted}
              isDirty={isDirty}
              isDepartmentDirty={!!dirtyFields.department}
              hasRouteAssignment={hasRouteAssignment}
            />
          </TabPanel>
          <TabPanel header="Объект">
            <AssetTab
              assetCardKey={assetCardKey}
              asset={asset}
              assetOptions={assetOptions}
              handleEditClick={handleEditClick}
            />
          </TabPanel>

          <TabPanel
            header={
              <span>
                Лента примечаний
                {hasNewComments && <Badge severity="info" className="ml-2" />}
              </span>
            }
            style={{
              display: taskId === 0 && hasTaskRole ? 'none' : 'block'
            }}
          >
            <TaskCommentsComponent
              taskId={parseInt(taskId?.toString())}
              setHasNewComments={setHasNewComments}
              setHasComments={setHasComments}
            />
          </TabPanel>

          <TabPanel
            header="Работы"
            className={worksTabErrors ? 'error-tab' : ''}
          >
            <OperationTabContent
              loading={loading}
              tagScrollRef={tagScrollRef}
              plannedOperations={plannedOperations}
              plannedOperationsOptions={plannedOperationsOptions}
              setValue={setValue}
              actualOperations={actualOperations}
              plannedOperationsAttainments={plannedOperationsAttainments}
              allOperationsAttainments={allOperationsAttainments}
              actualOperationsEmployeeOptions={actualOperationsEmployeeOptions}
              handleDeletePlannedJob={handleDetelePlannedJob}
              triggerApiCall={triggerApiCall}
              tagScrollHeight={tagScrollHeight}
              employees={employees}
              rawEmployeesData={rawExecutorsData}
              department={department}
              plannedOperationsErrorMessage={rootLevelFieldErrorMessage(
                errors.plannedOperations
              )}
              actualOperationsErrorMessage={rootLevelFieldErrorMessage(
                errors.actualOperations
              )}
              workOrderTimeframe={{
                start: plannedStartDate,
                end: plannedEndDate
              }}
            />
          </TabPanel>

          <TabPanel header="История">
            <TaskHistoryComponent
              key={triggerApiCall}
              taskHistory={taskHistory}
              loading={loading}
            />
          </TabPanel>

          <TabPanel
            className={attachmentsError ? 'error-tab' : ''}
            style={{ visibility: taskId === 0 ? 'hidden' : 'visible' }}
            header="Вложения"
          >
            <FileUpload
              taskId={parseInt(taskId?.toString())}
              onFilesUploaded={() =>
                getTaskApi(parseInt(taskId?.toString()), true)
              }
            />
          </TabPanel>
        </TabView>
        <TaskCardDialogs
          showDialog={showDialog}
          handleConfirmationCancel={handleConfirmationCancel}
          handleConfirmationContinue={handleConfirmationContinue}
          showEmployeesRecommendationDialog={showEmployeesRecommendationDialog}
          dismissEmployeesRecommendationDialog={
            dismissEmployeesRecommendationDialog
          }
          confirmEmployeesRecommendation={confirmEmployeesRecommendation}
          recommendationDialogContext={recommendationDialogContext}
          recommendedEmployeesCount={recommendedEmployeesCount}
          employees={employees}
          showDeleteDialog={showDeleteDialog}
          setShowDeleteDialog={setShowDeleteDialog}
          handleDeleteConfirmation={handleDeleteConfirmation}
          name={name}
          assetCardKey={assetCardKey}
          dialogVisible={dialogVisible}
          assetData={assetData}
          onHideAssetCard={onHideAssetCard}
          handleAdd={handleAdd}
          handleUpdate={handleUpdate}
          closeAssetDialog={closeAssetDialog}
          assetCardRef={assetCardRef}
          closeCodeDialogVisible={closeCodeDialogVisible}
          selectedAction={selectedAction}
          closingCodes={closingCodes}
          onHideTaskCodeCard={onHideTaskCodeCard}
          handleCodeDialogSave={handleCodeDialogSave}
          closeClosingCodesDialog={closeClosingCodesDialog}
          saveDialog={saveDialog}
          taskCodeCardRef={taskCodeCardRef}
          mapDialogVisible={mapDialogVisible}
          mapDialogLoading={mapDialogLoading}
          closeTaskMapDialog={closeTaskMapDialog}
          mapTaskDetails={mapTaskDetails}
          mapDialogError={mapDialogError}
        />
      </div>
    </Card>
  );
};

export default TaskCard;
